import { useEffect, useState, useCallback, useRef } from "react";
import { FaBars, FaTimes } from "react-icons/fa";

export interface NavItem {
  label: string;
  target: string;
}

interface HeaderProps {
  links: NavItem[];
  homeSection?: string;
  logo?: React.ReactNode;
  siteName?: string;
  onNavClick?: (target: string) => void;
}

export default function Header2({
  logo,
  siteName,
  links,
  homeSection = "home",
  onNavClick,
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(homeSection);
  const [isHydrated, setIsHydrated] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Setup intersection observer for active section detection
  useEffect(() => {
    if (!isHydrated) return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible?.target.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        threshold: 0.3,
        rootMargin: "-100px 0px -100px 0px",
      }
    );

    // Observe all sections
    links.forEach((link) => {
      const element = document.getElementById(link.target);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [links, isHydrated]);

  // Handle hash on load
  useEffect(() => {
    if (!isHydrated) return;

    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        timeoutRef.current = setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 150);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHydrated]);

  // Scroll to section function
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    // Close mobile menu if open
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    // Update URL hash without causing scroll
    window.history.replaceState(null, "", `#${sectionId}`);

    setActiveSection(sectionId);

    // Call external handler if provided
    if (onNavClick) {
      onNavClick(sectionId);
    }
  }, [isMobileMenuOpen, onNavClick]);

  // Toggle mobile menu
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Get link style based on state
  const getLinkStyle = useCallback((target: string) => {
    const isActive = activeSection === target;
    const baseStyle = "relative text-[13px] lg:text-[15px] uppercase tracking-[2px] lg:tracking-[3px] font-medium transition-all duration-200";
    
    if (isActive) {
      return `${baseStyle} text-blue-600 font-semibold`;
    }
    
    if (isScrolled) {
      return `${baseStyle} text-gray-700 hover:text-blue-500`;
    }
    
    return `${baseStyle} text-white hover:text-blue-200`;
  }, [activeSection, isScrolled]);

  // Get mobile link style
  const getMobileLinkStyle = useCallback((target: string) => {
    const isActive = activeSection === target;
    return `text-left py-3 uppercase tracking-[2px] border-b border-gray-100 transition-colors ${
      isActive
        ? "text-blue-600 border-blue-600 font-semibold"
        : "text-gray-700 hover:text-blue-600"
    }`;
  }, [activeSection]);

  // Get active indicator
  const getActiveIndicator = useCallback((target: string) => {
    if (activeSection === target) {
      return <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-blue-600" />;
    }
    return null;
  }, [activeSection]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6">
        {/* Logo */}
        <button
          type="button"
          onClick={() => scrollToSection(homeSection)}
          className={`flex items-center gap-2 shrink-0 transition-transform duration-200 hover:scale-105 ${
            isScrolled ? "text-indigo-800 font-extrabold" : "text-amber-100"
          }`}
          aria-label="Go to Home"
        >
          {logo}
          {siteName && (
            <span className="font-primary text-lg font-bold hidden sm:inline">
              {siteName}
            </span>
          )}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {links.map((item) => (
            <button
              key={item.target}
              type="button"
              onClick={() => scrollToSection(item.target)}
              className={getLinkStyle(item.target)}
            >
              {item.label}
              {getActiveIndicator(item.target)}
            </button>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={toggleMobileMenu}
          className="md:hidden text-2xl p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMobileMenuOpen ? (
            <FaTimes className={isScrolled ? "text-gray-800" : "text-white"} />
          ) : (
            <FaBars className={isScrolled ? "text-gray-800" : "text-white"} />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed left-0 right-0 top-[72px] bg-white shadow-2xl transition-all duration-300 transform ${
          isMobileMenuOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col p-6 space-y-1 max-h-[calc(100vh-72px)] overflow-y-auto">
          {links.map((item) => (
            <button
              key={item.target}
              type="button"
              onClick={() => scrollToSection(item.target)}
              className={getMobileLinkStyle(item.target)}
            >
              {item.label}
            </button>
          ))}
          
          {/* Optional: Add extra mobile footer items */}
          <div className="pt-4 mt-4 border-t border-gray-200">
            <div className="text-xs text-gray-400 text-center">
              {siteName || "Navigate"}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}